import React, { useState, useEffect, useRef } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import axios from "axios";
import { apiKey } from "../config/config";

export default function CustomerSideNav({
  lang = {},
  currentLangFunc,
  currentDir,
  dirFormat = "ltr",
}) {
  const [openNav, setOpenNav] = useState(false);
  const [customerName, setCustomerName] = useState("");
  const [notifications, setNotifications] = useState([]);
  const [openNotifications, setOpenNotifications] = useState(false);
  const dropdownRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    try {
      const session = sessionStorage.getItem("aman-customer");
      if (session) {
        const parsed = JSON.parse(session);
        setCustomerName(parsed.user?.fullName || "Customer");
      }
    } catch (e) {}
    fetchNotifications();
  }, []);

  // Close notifications dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setOpenNotifications(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const fetchNotifications = () => {
    const session = JSON.parse(sessionStorage.getItem("aman-customer") || "{}");
    const token = session.token;
    if (!token) return;

    axios
      .get(`${apiKey}/customer-panel/notifications`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => {
        setNotifications(res.data.notifications || []);
      })
      .catch((err) => console.error(err));
  };

  const markRead = (id) => {
    const session = JSON.parse(sessionStorage.getItem("aman-customer") || "{}");
    const token = session.token;
    if (!token) return;

    axios
      .put(
        `${apiKey}/customer-panel/notifications/${id}/read`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      )
      .then(() => {
        fetchNotifications();
      })
      .catch((err) => console.error(err));
  };

  const handleLogout = () => {
    sessionStorage.removeItem("aman-customer");
    navigate("/portal-login");
  };

  const unreadCount = notifications.filter((n) => !n.is_read).length;
  const isRtl = dirFormat === "rtl";

  return (
    <>
      <div>
        <button
          onClick={() => setOpenNav(!openNav)}
          className="btn bg-white shadow position-fixed d-lg-none"
          style={{ top: "50px", zIndex: "50" }}
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
            <path d="M4 6h16v2H4zm0 5h16v2H4zm0 5h16v2H4z"></path>
          </svg>
        </button>
        {openNav && <div className="navBg"></div>}
        <aside
          id="sidebar"
          className={`sidebar ${openNav ? "active" : ""} ${
            isRtl ? "sidebar-right" : "sidebar-left"
          }`}
        >
          <button
            className="btn position-absolute d-lg-none"
            style={{ top: "10px", right: "10px" }}
            onClick={() => setOpenNav(false)}
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
              <path d="m16.192 6.344-4.243 4.242-4.242-4.242-1.414 1.414L10.535 12l-4.242 4.242 1.414 1.414 4.242-4.242 4.243 4.242 1.414-1.414L13.364 12l4.242-4.242z"></path>
            </svg>
          </button>
          <NavLink to="/customer-panel" className="logo d-flex align-items-center">
            <img src="/img/logo.png" alt="logo" />
          </NavLink>

          {/* Profile Header & Floating Notifications */}
          <div className="w-100 text-center py-2 my-2 border-bottom" ref={dropdownRef}>
            <h6 className="fw-bold mb-1 text-primary">{customerName}</h6>
            <div className="d-flex align-items-center justify-content-center gap-2 mt-1">
              <span className="badge bg-light text-secondary border">Customer Portal</span>
              
              {/* Notification Icon Button */}
              <button
                type="button"
                className="btn btn-sm btn-light border rounded-circle position-relative p-0"
                style={{
                  width: "32px",
                  height: "32px",
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  background: unreadCount > 0 ? "#e7f3ff" : "#f8f9fa",
                }}
                onClick={() => setOpenNotifications(!openNotifications)}
                title="Notifications"
              >
                <i className={`fa fa-bell ${unreadCount > 0 ? "text-primary" : "text-secondary"}`} style={{ fontSize: "0.9rem" }}></i>
                {unreadCount > 0 && (
                  <span
                    className="position-absolute top-0 start-100 translate-middle badge rounded-pill bg-danger"
                    style={{ fontSize: "0.6rem", padding: "0.2em 0.45em" }}
                  >
                    {unreadCount}
                  </span>
                )}
              </button>

              {/* Position: Fixed Dropdown Card (Bypasses overflow clipping) */}
              {openNotifications && (
                <div
                  className="card border-0 shadow-lg text-start rounded-4 position-fixed"
                  style={{
                    top: "135px",
                    left: isRtl ? "auto" : "15px",
                    right: isRtl ? "15px" : "auto",
                    width: "270px",
                    zIndex: 99999,
                    background: "#ffffff",
                    boxShadow: "0 12px 35px rgba(0, 0, 0, 0.22), 0 4px 10px rgba(0, 0, 0, 0.1)",
                  }}
                >
                  <div className="card-header bg-white border-bottom py-2 px-3 d-flex justify-content-between align-items-center rounded-top-4">
                    <h6 className="fw-bold m-0 text-dark" style={{ fontSize: "0.88rem" }}>
                      Notifications
                    </h6>
                    {unreadCount > 0 ? (
                      <span className="badge bg-primary rounded-pill" style={{ fontSize: "0.65rem" }}>
                        {unreadCount} unread
                      </span>
                    ) : (
                      <span className="small text-muted" style={{ fontSize: "0.7rem" }}>All read</span>
                    )}
                  </div>
                  <div
                    className="card-body p-2"
                    style={{ maxHeight: "300px", overflowY: "auto" }}
                  >
                    {notifications.length === 0 ? (
                      <div className="text-center py-4 text-muted">
                        <i className="fa fa-bell-slash fs-4 d-block mb-1 text-secondary opacity-50"></i>
                        <span className="small">No notifications yet</span>
                      </div>
                    ) : (
                      notifications.map((n) => (
                        <div
                          key={n.id}
                          className={`p-2 mb-2 rounded-3 border ${
                            n.is_read ? "bg-light text-secondary" : "bg-white border-primary shadow-sm"
                          }`}
                          style={{ fontSize: "0.8rem" }}
                        >
                          <div className="d-flex justify-content-between align-items-start gap-1">
                            <strong className="text-dark small d-block mb-1">{n.title}</strong>
                            {!n.is_read && (
                              <button
                                className="btn btn-sm btn-link p-0 text-primary fw-bold text-decoration-none"
                                style={{ fontSize: "0.68rem" }}
                                onClick={() => markRead(n.id)}
                              >
                                Mark read
                              </button>
                            )}
                          </div>
                          <p className="mb-1 text-secondary" style={{ fontSize: "0.75rem", lineHeight: "1.3" }}>
                            {n.message}
                          </p>
                          <span className="text-muted d-block" style={{ fontSize: "0.62rem" }}>
                            {n.created_at ? new Date(n.created_at).toLocaleString() : ""}
                          </span>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          <ul className="sidebar-nav" id="sidebar-nav">
            <li className="nav-item">
              <NavLink
                className="nav-link collapsed"
                to="/customer-panel"
                end
                style={({ isActive }) => ({
                  color: isActive ? "#0F60FF" : "#000000",
                  background: isActive ? "#f6f9ff" : "",
                })}
              >
                <img src="/img/dashboard.png" alt="Dashboard" className="sidebaricon" />
                <span>{lang.Dashboard || "Dashboard"}</span>
              </NavLink>
            </li>

            <li className="nav-heading">Contract Services</li>

            <li className="nav-item">
              <NavLink
                className="nav-link collapsed"
                to="/customer-panel/contracts"
                style={({ isActive }) => ({
                  color: isActive ? "#0F60FF" : "#000000",
                  background: isActive ? "#f6f9ff" : "",
                })}
              >
                <img src="/img/customers.png" alt="Contracts" className="sidebaricon" />
                <span>{lang.My_Contracts || "My Contracts"}</span>
              </NavLink>
            </li>

            <li className="nav-item">
              <NavLink
                className="nav-link collapsed"
                to="/customer-panel/apply-contract"
                style={({ isActive }) => ({
                  color: isActive ? "#0F60FF" : "#000000",
                  background: isActive ? "#f6f9ff" : "",
                })}
              >
                <img src="/img/contracts.png" alt="logo" className="sidebaricon" />
                <span>{lang.Apply_For_Contract || "Apply for Contract"}</span>
              </NavLink>
            </li>

            <li className="nav-item">
              <NavLink
                className="nav-link collapsed"
                to="/customer-panel/applications"
                style={({ isActive }) => ({
                  color: isActive ? "#0F60FF" : "#000000",
                  background: isActive ? "#f6f9ff" : "",
                })}
              >
                <img src="/img/report.png" alt="logo" className="sidebaricon" />
                <span>{lang.Application_Status || "Application Status"}</span>
              </NavLink>
            </li>

            <li className="nav-heading">Account & Reports</li>

            <li className="nav-item">
              <NavLink
                className="nav-link collapsed"
                to="/customer-panel/reports"
                style={({ isActive }) => ({
                  color: isActive ? "#0F60FF" : "#000000",
                  background: isActive ? "#f6f9ff" : "",
                })}
              >
                <img src="/img/inventory.png" alt="logo" className="sidebaricon" />
                <span>{lang.Reports || "My Reports"}</span>
              </NavLink>
            </li>

            <li className="nav-item">
              <NavLink
                className="nav-link collapsed"
                to="/customer-panel/profile"
                style={({ isActive }) => ({
                  color: isActive ? "#0F60FF" : "#000000",
                  background: isActive ? "#f6f9ff" : "",
                })}
              >
                <i className="fa fa-user-gear sidebaricon fs-5 text-secondary me-0"></i>
                <span>{lang.Profile || "My Profile"}</span>
              </NavLink>
            </li>

            <li className="nav-item mt-4">
              <button
                className="nav-link collapsed w-100 text-start border-0 bg-transparent text-danger"
                onClick={handleLogout}
              >
                <i className="fa fa-right-from-bracket sidebaricon fs-5 text-danger me-0"></i>
                <span>{lang.Logout || "Logout"}</span>
              </button>
            </li>
          </ul>
        </aside>
      </div>
    </>
  );
}
